import type {
  PrinterAdapter,
  PrinterDevice,
  PrinterSession,
  PrinterStatus,
  LabelTemplate,
} from '@trackeat/shared';
import { renderTemplate, type RenderedElement } from './renderer.js';
import { BrotherQl, type BrotherPrintOptions } from './brother-plugin.js';

const DPI = 300;
const MM_PER_INCH = 25.4;

const mmToPx = (mm: number): number => Math.round((mm / MM_PER_INCH) * DPI);

const pickLabelSize = (
  widthMm: number,
  heightMm: number,
): BrotherPrintOptions['labelSize'] => {
  if (Math.abs(widthMm - 62) < 2 && Math.abs(heightMm - 100) < 2) return 'DK_11202';
  if (Math.abs(widthMm - 38) < 2 && Math.abs(heightMm - 90) < 2) return 'DK_11208';
  if (Math.abs(widthMm - 17) < 2 && Math.abs(heightMm - 54) < 2) return 'DK_11204';
  if (Math.abs(widthMm - 29) < 2 && Math.abs(heightMm - 90) < 2) return 'DK_11201';
  return 'DK_22205';
};

interface Raster {
  widthPx: number;
  heightPx: number;
  bytes: Uint8Array;
}

const createRaster = (widthMm: number, heightMm: number): Raster => {
  const widthPx = Math.ceil(mmToPx(widthMm) / 8) * 8;
  const heightPx = mmToPx(heightMm);
  const bytes = new Uint8Array((widthPx / 8) * heightPx);
  return { widthPx, heightPx, bytes };
};

const toBase64 = (bytes: Uint8Array): string => {
  if (typeof btoa === 'function') {
    let s = '';
    for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]!);
    return btoa(s);
  }
  throw new Error('btoa non disponibile nel runtime corrente');
};

const rasterize = (
  template: LabelTemplate,
  rendered: RenderedElement[],
): Raster => {
  const raster = createRaster(template.width_mm, template.height_mm);
  // TODO: implement proper 1-bit rasterization:
  //  - PWA: draw rendered[] onto OffscreenCanvas(widthPx,heightPx) and
  //    threshold getImageData() to 1 bit packed 8 pixels per byte.
  //  - Native iOS/Android: push rendered[] to plugin as drawing ops and let
  //    native code produce the raster via CoreGraphics / Android Canvas.
  //  For now we return an empty (all-white) buffer so the print pipeline
  //  is fully structured.
  void rendered;
  return raster;
};

export class BrotherQlAdapter implements PrinterAdapter {
  readonly driver = 'brother_ql' as const;

  async discover(): Promise<PrinterDevice[]> {
    const { devices } = await BrotherQl.discover();
    return devices.map((d) => ({
      id: d.id,
      name: d.name,
      driver: this.driver,
      connection: d.connection,
      address: d.address,
      model_hint: d.model,
    }));
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    await BrotherQl.connect({ deviceId: device.id });
    return { deviceId: device.id, driver: this.driver };
  }

  async disconnect(session: PrinterSession): Promise<void> {
    await BrotherQl.disconnect({ deviceId: session.deviceId });
  }

  async printLabel(
    session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies: number = 1,
  ): Promise<void> {
    const rendered = renderTemplate(template, data);
    const raster = rasterize(template, rendered);
    const rasterBytesB64 = toBase64(raster.bytes);
    const result = await BrotherQl.print({
      deviceId: session.deviceId,
      rasterBytesB64,
      widthPx: raster.widthPx,
      heightPx: raster.heightPx,
      copies: Math.max(1, copies),
      labelSize: pickLabelSize(template.width_mm, template.height_mm),
    });
    if (!result.success) {
      throw new Error(`Stampa Brother fallita: ${result.errorCode ?? 'sconosciuto'}`);
    }
  }

  async getStatus(session: PrinterSession): Promise<PrinterStatus> {
    const s = await BrotherQl.status({ deviceId: session.deviceId });
    return {
      online: s.online,
      paper_ok: s.paperOk,
      cover_closed: s.coverClosed,
      battery_pct: s.batteryPct,
      error_code: s.errorCode,
    };
  }
}
